import React, { useEffect } from 'react';
import { useHistory } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { useToggle } from 'react-use';
import { Layout as AntLayout, Menu } from 'antd';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import split from 'lodash/split';
import cn from 'classnames';

import { pushOpenedId } from '../../../actions/sidebar';
import { getOpenedIds } from '../../../selectors/sidebar';
import { Path } from '../../../routes';
import Brand from '@components/layout/Brand';
import SidebarItem from '@components/layout/Sidebar/SidebarItem';
import PlaylistsMenu from '../../../pages/PlaylistPage/PlaylistsMenu';
import CalendarMenu from '../../../pages/CalendarPage/CalendarMenu';
import { FileOutlined } from '@ant-design/icons';
import DevicesIcon from '@assets/img/devices.svg';
import HelpIcon from '@assets/img/help.svg';
import SettingsIcon from '@assets/img/settings.svg';
import ExitIcon from '@assets/img/exit.svg';
import { makeFetchEntityByIdSelector } from '../../../selectors/fetch';

const { Sider } = AntLayout;

const MenuId = {
  item_1: 'playlist',
  item_2: 'calendar',
};

const MenuKey = {
  playlist: 'item_1',
  calendar: 'item_2',
};

function Sidebar() {
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const openedIds = useSelector(getOpenedIds());
  const [isOpen, toggle] = useToggle(false);
  const { t } = useTranslation();
  const [, routeName, routeId] = split(location.pathname, '/');
  const { response: settings = {} } = useSelector(
    makeFetchEntityByIdSelector('settings')
  );

  const onMenuSelect = ({ key }) => {
    dispatch(pushOpenedId(MenuId[key] || key));
  };

  const onLogout = () => {
    window.localStorage.removeItem('_token');
    history.replace('/login');
  };

  useEffect(() => {
    if (!openedIds) {
      dispatch(pushOpenedId(routeName));
    }
  }, []);

  return (
    <Sider width={256} className="layout__sidebar sidebar">
      <Brand
        className="sidebar__brand"
        theme="light"
        isOpen={isOpen}
        toggle={toggle}
        onClick={() => dispatch(pushOpenedId('docs'))}
      />
      <div
        className={cn('sidebar__menu-container', {
          'sidebar__menu-container--opened': isOpen,
        })}
      >
        <Menu
          onSelect={onMenuSelect}
          key="menu-1"
          className="sidebar__nav"
          theme="dark"
          mode="inline"
          openKeys={[MenuKey[openedIds]]}
          forceSubMenuRender
        >
          <SidebarItem
            className={cn({
              'ant-menu-item-selected': openedIds === 'content',
            })}
            id="content"
            key="content"
            href={Path.CONTENT}
            icon={<FileOutlined width={16} height={16} />}
            text={t('content')}
          />

          <PlaylistsMenu
            routeName={routeName}
            routeId={routeId}
            openId={openedIds}
            onSelect={onMenuSelect}
          />

          <CalendarMenu
            routeName={routeName}
            routeId={routeId}
            openId={openedIds}
            onSelect={onMenuSelect}
          />

          <SidebarItem
            id="devices"
            key="devices"
            className={cn({
              'ant-menu-item-selected': openedIds === 'devices',
            })}
            href={Path.DEVICES}
            icon={
              <DevicesIcon
                className="sidebar__nav-icon"
                width={16}
                height={16}
              />
            }
            text={t('devices')}
          />

          <SidebarItem
            id="docs"
            key="docs"
            className={cn('sidebar__nav-item--docs', {
              'ant-menu-item-selected': openedIds === 'docs',
            })}
            href={Path.DOCS}
            icon={
              <HelpIcon className="sidebar__nav-icon" width={16} height={16} />
            }
            text={t('docs')}
          />

          <SidebarItem
            id="settings"
            className="sidebar__nav-item--settings"
            href={Path.SETTINGS}
            icon={
              <SettingsIcon
                className="sidebar__nav-icon"
                width={16}
                height={16}
              />
            }
            text={
              <>
                {settings.companyName && (
                  <>
                    {settings.companyName}
                    <br />
                  </>
                )}
                {settings.email}
              </>
            }
          />

          <SidebarItem
            id="exit"
            href={Path.LOGOUT}
            icon={
              <ExitIcon className="sidebar__nav-icon" width={16} height={16} />
            }
            onClick={onLogout}
            text={t('exit')}
          />
        </Menu>
      </div>
    </Sider>
  );
}

export default Sidebar;
